import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, Subject, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../../core/services/auth.service';


@Injectable({
    providedIn: 'root'
})
export class ProductoService {
    private apiUrl = `${environment.API_SECURITY}/productos`;

    /** Emitido cuando llegan productos actualizados por socket (find-products-by-ids). */
    private readonly productsUpdatedSubject = new Subject<any[]>();
    readonly productsUpdated$ = this.productsUpdatedSubject.asObservable();

    constructor(
        private http: HttpClient,
        private auth: AuthenticationService
    ) { }

    findProductsByIds(idProductos: number[]): Observable<any[]> {
        const token = this.auth?.getToken?.();
        const t = token != null && token !== '' ? (typeof token === 'string' ? token : (token as any)?.token ?? '') : '';
        const headers: { [key: string]: string } = {};
        if (t) headers['Authorization'] = `Bearer ${t}`;

        return this.http.post<any>(`${this.apiUrl}/find-products-by-ids`, { idProductos }, { headers }).pipe(
            catchError(err => {
                console.warn('[ProductoService] find-products-by-ids error:', err?.status ?? err);
                return throwError(() => err);
            }),
            map((res: any) => Array.isArray(res) ? res : (res?.data ?? res?.productos ?? []))
        );
    }

    /**
     * Notifica que debe refrescarse la lista (p. ej. socket `find-products-by-ids`).
     * Emite siempre para disparar POST `/productos/paginated` aunque el payload venga vacío o solo ids.
     */
    notifyProductsUpdated(products: any[] | null | undefined): void {
        const list = Array.isArray(products) ? products : [];
        this.productsUpdatedSubject.next(list);
    }


    obtenerProductos(): Observable<any[]> {
        return this.http.get<any[]>(environment.API_SECURITY + '/productos');
    }

    agregarProducto(saveForm) {
        return this.http.post(environment.API_SECURITY + '/productos', saveForm);
    }

    obtenerProducto(idProducto: number): Observable<any> {
        return this.http.get<any>(environment.API_SECURITY + '/productos/' + idProducto);
    }

    actualizarProducto(saveForm: any): Observable<any> {
        return this.http.put(`${environment.API_SECURITY}/productos`, saveForm);
    }

    eliminarProducto(idProducto: number) {
        return this.http.delete(environment.API_SECURITY + '/productos/' + idProducto);
    }

    updateEstatus(id: number | string): Observable<string> {
        const numericId = Number(id); // <-- fuerza conversión a número
        const url = `${this.apiUrl}/${numericId}`;

        return this.http.delete(url, { responseType: 'text' }).pipe(
            catchError(error => throwError(() => error))
        );
    }

    activar(id: number | string): Observable<string> {
        const numericId = Number(id);
        const url = `${this.apiUrl}/activar/${numericId}`;

        return this.http.patch(url, {}, { responseType: 'text' }).pipe(
            catchError(error => throwError(() => error))
        );
    }

    /**
     * POST `/productos/paginated` — cuerpo: page, limit; opcionales idSucursal, busqueda (máx. 20 caracteres en cliente).
     */
    obtenerProductosData(
        page: number,
        pageSize: number,
        filtros?: { idSucursal?: number | null; busqueda?: string | null }
    ): Observable<any> {
        const body: Record<string, number | string> = {
            page,
            limit: pageSize,
        };
        const idS = filtros?.idSucursal;
        if (idS != null && Number.isFinite(Number(idS))) {
            body['idSucursal'] = Number(idS);
        }
        const q = (filtros?.busqueda ?? '').toString().trim().slice(0, 20);
        if (q.length > 0) {
            body['busqueda'] = q;
        }
        return this.http.post(`${environment.API_SECURITY}/productos/paginated`, body);
    }

    /** GET plantilla Excel para carga masiva de productos */
    descargarPlantillaMasivo(): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/masivo/plantilla`, { responseType: 'blob' });
    }

    /** POST importación masiva; solo .xlsx (el caller valida extensión) */
    importarPlantillaMasivo(file: File): Observable<unknown> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.http.post(`${this.apiUrl}/masivo/importar`, formData);
    }
}
